import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const SECTIONS = [
  { id: 'identificacion', label: '1. Identificación del vendedor' },
  { id: 'objeto', label: '2. Objeto y ámbito de aplicación' },
  { id: 'pedidos', label: '3. Pedidos y formación del contrato' },
  { id: 'precios', label: '4. Precios, IVA e impuestos' },
  { id: 'pago', label: '5. Medios de pago' },
  { id: 'envios', label: '6. Envíos, plazos y tarifas' },
  { id: 'desistimiento', label: '7. Derecho de desistimiento (14 días)' },
  { id: 'devoluciones', label: '8. Gastos de devolución' },
  { id: 'cambios', label: '9. Cambios' },
  { id: 'reembolsos', label: '10. Reembolsos' },
  { id: 'excluidos', label: '11. Productos excluidos del desistimiento' },
  { id: 'garantia', label: '12. Garantía y productos defectuosos' },
  { id: 'responsabilidad', label: '13. Responsabilidad' },
  { id: 'datos', label: '14. Protección de datos' },
  { id: 'conflictos', label: '15. Resolución de conflictos' },
  { id: 'modificaciones', label: '16. Modificaciones' },
];

export default function TermsConditions() {
  return (
    <div className="min-h-screen bg-white">
      <div className="bg-black text-white py-10 px-6">
        <div className="container mx-auto max-w-4xl">
          <Link to="/" className="text-gray-300 hover:text-white text-sm inline-flex items-center gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: 'Playfair Display' }}>
            Condiciones Generales de Venta
          </h1>
          <p className="text-gray-400 mt-2 text-sm">Última actualización: 27 de abril de 2026</p>
        </div>
      </div>

      <div className="container mx-auto max-w-4xl px-6 py-12 grid lg:grid-cols-[240px_1fr] gap-10">
        {/* TOC */}
        <nav className="hidden lg:block sticky top-4 self-start text-sm">
          <p className="font-bold mb-3 text-xs uppercase tracking-widest text-gray-500">Índice</p>
          <ul className="space-y-1.5">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-gray-600 hover:text-black">
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <article className="prose prose-neutral max-w-none text-gray-800 leading-relaxed" data-testid="terms-article">
          <section id="identificacion">
            <h2 className="text-2xl font-bold mt-0">1. Identificación del vendedor</h2>
            <ul className="list-none pl-0 space-y-1">
              <li><strong>Denominación social:</strong> ARTHUR FITZ S.L.</li>
              <li><strong>CIF:</strong> B86252848</li>
              <li><strong>Domicilio fiscal:</strong> Gran Vía 46, 9A — 28013 Madrid (España)</li>
              <li><strong>Correo electrónico:</strong> info@arthurfitz.com</li>
              <li><strong>Teléfono:</strong> +34 649 471 889</li>
              <li><strong>Sitio web:</strong> www.arthurfitz.com</li>
            </ul>
          </section>

          <section id="objeto">
            <h2 className="text-2xl font-bold mt-10">2. Objeto y ámbito de aplicación</h2>
            <p>
              Las presentes Condiciones Generales de Venta (en adelante, "CGV") regulan la
              adquisición de productos a través del sitio web de ARTHUR FITZ S.L. por parte de
              cualquier usuario mayor de edad con plena capacidad de obrar (en adelante, el
              "Cliente"). El hecho de realizar un pedido implica la aceptación expresa e
              íntegra de estas CGV.
            </p>
          </section>

          <section id="pedidos">
            <h2 className="text-2xl font-bold mt-10">3. Pedidos y formación del contrato</h2>
            <p>
              El Cliente seleccionará los productos, introducirá los datos requeridos,
              escogerá el método de pago y confirmará el pedido. ARTHUR FITZ S.L. enviará
              un correo electrónico de confirmación. El contrato queda perfeccionado con
              dicha confirmación y el cobro efectivo del pedido.
            </p>
          </section>

          <section id="precios">
            <h2 className="text-2xl font-bold mt-10">4. Precios, IVA e impuestos</h2>
            <p>
              Todos los precios se muestran en Euros e incluyen el IVA aplicable en España
              (21%), salvo indicación expresa. ARTHUR FITZ S.L. se reserva el derecho a
              modificar precios en cualquier momento; los precios aplicables serán los
              vigentes en el momento de la confirmación del pedido.
            </p>
          </section>

          <section id="pago">
            <h2 className="text-2xl font-bold mt-10">5. Medios de pago</h2>
            <p>Se aceptan los siguientes métodos:</p>
            <ul className="list-disc pl-6">
              <li>Tarjeta de crédito/débito a través de Stripe (pasarela con certificación PCI-DSS).</li>
              <li>Bizum.</li>
              <li>Transferencia bancaria (pedido en pendiente hasta verificar ingreso).</li>
            </ul>
          </section>

          <section id="envios">
            <h2 className="text-2xl font-bold mt-10">6. Envíos, plazos y tarifas</h2>
            <p>
              Las tarifas y plazos de entrega vigentes aparecen de forma dinámica en el
              momento del checkout y en nuestra página de información de envíos. Para
              conocer las tarifas actualizadas, consulta{' '}
              <Link to="/envios" className="text-black underline">nuestra información de envíos</Link>.
              Los plazos se computan en días laborables desde la confirmación del pago.
            </p>
          </section>

          <section id="desistimiento">
            <h2 className="text-2xl font-bold mt-10">7. Derecho de desistimiento (14 días)</h2>
            <p>
              De conformidad con el artículo 102 del Real Decreto Legislativo 1/2007 (LGDCU),
              el Cliente dispone de <strong>14 días naturales</strong> desde la recepción de
              la mercancía para desistir del contrato sin necesidad de justificación. Para
              ejercer el derecho, deberá comunicarlo por correo electrónico a{' '}
              <a href="mailto:info@arthurfitz.com" className="text-black underline">
                info@arthurfitz.com
              </a>{' '}
              indicando número de pedido y productos a devolver.
            </p>
            <p>
              La mercancía debe devolverse en su embalaje original, sin usar y en perfecto
              estado de reventa.
            </p>
          </section>

          <section id="devoluciones">
            <h2 className="text-2xl font-bold mt-10">8. Gastos de devolución</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Error por nuestra parte</strong> (producto defectuoso, envío incorrecto,
                etc.): <strong>sin coste</strong> para el Cliente. Te enviaremos una etiqueta
                prepagada para la devolución.
              </li>
              <li>
                <strong>Desistimiento — envío por tu cuenta</strong>: el Cliente asume los
                gastos del envío al remitir la mercancía a nuestras instalaciones
                (Gran Vía 46, 9A — 28013 Madrid). <strong>No se descontará importe adicional</strong>
                {' '}del reembolso.
              </li>
              <li>
                <strong>Desistimiento — etiqueta prepagada o recogida en domicilio</strong>:
                si solicitas que gestionemos la recogida o te enviemos etiqueta,
                se aplicará un cargo de <strong>5,50 €</strong> que se descontará del importe
                del reembolso.
              </li>
            </ul>
          </section>

          <section id="cambios">
            <h2 className="text-2xl font-bold mt-10">9. Cambios</h2>
            <p>Los cambios se tramitan como una devolución seguida de un nuevo pedido:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Gastos de envío del nuevo artículo</strong>: <strong>4,50 €</strong>.
              </li>
              <li>
                <strong>Devolución del artículo original</strong> — si el Cliente lo envía por
                su cuenta: sin coste adicional (total: 4,50 € del nuevo envío).
              </li>
              <li>
                <strong>Recogida en domicilio del artículo original</strong>: <strong>5,50 €</strong>
                {' '}adicionales (total: 10 €).
              </li>
            </ul>
          </section>

          <section id="reembolsos">
            <h2 className="text-2xl font-bold mt-10">10. Reembolsos</h2>
            <p>
              Una vez recibida y verificada la mercancía en nuestras instalaciones, se
              emitirá el reembolso en un plazo de <strong>24 a 72 horas</strong> a través del
              mismo medio de pago utilizado en la compra (salvo acuerdo expreso en otro
              sentido). El Cliente recibirá confirmación por correo electrónico.
            </p>
          </section>

          <section id="excluidos">
            <h2 className="text-2xl font-bold mt-10">11. Productos excluidos del derecho de desistimiento</h2>
            <p>
              De acuerdo con el artículo 103 LGDCU, <strong>NO se admite desistimiento</strong>{' '}
              en los siguientes casos:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Pelucas y prótesis capilares <strong>personalizadas</strong> o confeccionadas
                a medida según las especificaciones del Cliente.
              </li>
              <li>
                Artículos que por razones de <strong>higiene o protección de la salud</strong>
                {' '}hayan sido desprecintados o usados tras la entrega: cintas adhesivas,
                sistemas adhesivos abiertos, productos de mantenimiento en uso.
              </li>
              <li>Productos deteriorados tras la recepción por uso distinto al esperado.</li>
            </ul>
            <p>
              En todos los casos, el derecho de desistimiento sí aplica si el producto
              presenta un <strong>defecto de fábrica</strong> o llega dañado.
            </p>
          </section>

          <section id="garantia">
            <h2 className="text-2xl font-bold mt-10">12. Garantía y productos defectuosos</h2>
            <p>
              Los productos cuentan con la garantía legal de conformidad de <strong>3 años</strong>
              {' '}para bienes muebles (art. 120 LGDCU, redacción RD-ley 7/2021). En caso de
              no conformidad, el Cliente tiene derecho a reparación, sustitución, rebaja
              del precio o resolución del contrato, según proceda.
            </p>
          </section>

          <section id="responsabilidad">
            <h2 className="text-2xl font-bold mt-10">13. Responsabilidad</h2>
            <p>
              ARTHUR FITZ S.L. responderá por los daños directos derivados del
              incumplimiento de sus obligaciones hasta el límite del precio del pedido.
              No responderá por daños indirectos, lucro cesante ni causas de fuerza mayor.
            </p>
          </section>

          <section id="datos">
            <h2 className="text-2xl font-bold mt-10">14. Protección de datos</h2>
            <p>
              El tratamiento de datos personales se rige por nuestra{' '}
              <Link to="/privacidad" className="text-black underline">
                Política de Privacidad
              </Link>
              , conforme al RGPD (UE) 2016/679 y a la LOPDGDD 3/2018.
            </p>
          </section>

          <section id="conflictos">
            <h2 className="text-2xl font-bold mt-10">15. Resolución de conflictos</h2>
            <p>
              Para cualquier discrepancia, el Cliente podrá acudir a la plataforma europea
              de <strong>Resolución de Litigios en Línea (ODR)</strong> de la Comisión Europea:
              {' '}
              <a
                href="https://ec.europa.eu/consumers/odr/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-black underline break-all"
              >
                https://ec.europa.eu/consumers/odr/
              </a>
              {' '}(Reglamento UE 524/2013). Subsidiariamente, serán competentes los Juzgados
              y Tribunales de <strong>Madrid</strong>, con renuncia expresa a cualquier otro
              fuero que pudiera corresponder.
            </p>
          </section>

          <section id="modificaciones">
            <h2 className="text-2xl font-bold mt-10">16. Modificaciones</h2>
            <p>
              ARTHUR FITZ S.L. se reserva el derecho a modificar estas CGV en cualquier
              momento. Las condiciones aplicables a cada pedido serán las vigentes en el
              momento de su confirmación.
            </p>
          </section>

          <div className="mt-14 pt-6 border-t border-gray-200 text-sm text-gray-500">
            <p>
              ¿Tienes alguna duda? Escríbenos a{' '}
              <a href="mailto:info@arthurfitz.com" className="text-black underline">
                info@arthurfitz.com
              </a>
              {' '}o llámanos al +34 649 471 889.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
}
